import {
  CommonHelperFunctions,
  ESdChangeReason,
  ESymbolCategory,
  IComponent,
  IDesign,
  IDisplayItem,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IProject,
  IPropertyDefinition,
  IScreenDesign
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import PropertyRowIcon from "./property-row-icon";

export interface IPressEnterPanelState {
  isPressEnterPanelActive: boolean;
}

export interface IPressEnterPanelProps {}

interface IExpandIconProps {
  isActive?: boolean;
}

class PressEnterPanel extends PureSdComponent<IPressEnterPanelProps & IWithSDProps, IPressEnterPanelState> {
  constructor(props: IPressEnterPanelProps & IWithSDProps) {
    super(props);
    this.state = {
      isPressEnterPanelActive: false
    };
  }

  getAllDi = () => {
    let allDi: IDisplayItem[] = [];
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    this.fillAllDisplayItemsRecursive(sd, allDi);
    allDi = this.filterDisplayItemsByValueProperty(allDi);
    allDi.sort((a, b) =>
      (a.friendlyName || "").localeCompare(b.friendlyName || "", undefined, {
        numeric: true,
        sensitivity: "base"
      })
    );
    allDi = allDi.filter(this.isBigEnough);
    return allDi;
  };

  isBigEnough(value) {
    return value.item.name?.toLowerCase() === "button";
  }

  getPropertyDefinition = (
    diComponent: IDisplayItemComponent,
    diProperty: IDisplayItemProperty,
    project: IProject
  ): IPropertyDefinition | undefined => {
    if (project.componentSetObj && project.componentSetObj.components) {
      const component: IComponent | undefined = CommonHelperFunctions.findComponentById(
        diComponent?.id,
        project.componentSetObj.components
      );
      if (component) {
        const property = component.properties.find((p) => p.name === diProperty.name);
        if (property) {
          const result: IPropertyDefinition = {
            description: property.description,
            hasOptions: property.hasOptions,
            isRequired: property.isRequired,
            isTechnical: property.isTechnical,
            // isEvent: property.isEvent,
            isVisibleInFieldPicker: property.isVisibleInFieldPicker,
            isValueProp: property.isValueProp,
            isOptionsProp: property.isOptionsProp,
            isLanguageOptionsProp: property.isLanguageOptionsProp,
            isEditabilityProp: property.isEditabilityProp,
            isVisibleProp: property.isVisibleProp,
            isAdvanced: property.isAdvanced,
            isIgnoredOnCodeOutput: property.isIgnoredOnCodeOutput,
            isIgnoredOnGeneratedApp: property.isIgnoredOnGeneratedApp,
            isSupportMultiLanguage: property.isSupportMultiLanguage,
            appSupportType: property.appSupportType,
            isDeprecated: property.isDeprecated,
            name: property.name,
            options: property.options,
            tagName: property.tagName,
            type: property.type,
            typeOptions: property.typeOptions,
            defaultValue: property.symbol?.category === ESymbolCategory.fixed ? property.symbol.fixedValue : "",
            isChartOptionsProp: property.isChartOptionsProp,
            isTimelineOptionsProp: property.isTimelineOptionsProp,
            isChatOptionsProp: property.isChatOptionsProp,
            isTreeOptionsProp: property.isTreeOptionsProp,

            isCalendarOptionsProp: property.isCalendarOptionsProp,
            isMenuOptionsProp: property.isMenuOptionsProp,
            isBreadcrumbOptionsProp: property.isBreadcrumbOptionsProp,
            isRadialChartOptionsProp: property.isRadialChartOptionsProp,
            isRadialChartLevelOptionsProp: property.isRadialChartLevelOptionsProp,
            isMixedChartOptionsProp: property.isMixedChartOptionsProp,
            isMixedChartLineOptionsProp: property.isMixedChartLineOptionsProp,
            isMixedChartPointOptionsProp: property.isMixedChartPointOptionsProp,
            isMixedChartBarOptionsProp: property.isMixedChartBarOptionsProp,
            isKanbanBoardCardOptionsProp: property.isKanbanBoardCardOptionsProp,
            isKanbanBoardLaneOptionsProp: property.isKanbanBoardLaneOptionsProp,
            isNodeTreeNodeOptionsProp: property.isNodeTreeNodeOptionsProp,
            inVisibleStyles: property.inVisibleStyles,
            hideFixedValue: property.hideFixedValue
          };
          return result;
        }
      }
    }
  };

  /** The kit's own turning chevron, carrying the test id the E2E suite opens this section by. */
  renderExpandIcon = (panelProps: IExpandIconProps): ReactNode => (
    <KuikaSvg
      data-testid="screenPressEnterPropertyChevron"
      name="chevron-right"
      type="arrows"
      width={16}
      height={16}
      className={classNames("kuika-collapse-panel__chevron", {
        "kuika-collapse-panel__chevron--open": panelProps.isActive
      })}
    />
  );

  fillAllDisplayItemsRecursive = (sd: IDesign, result: IDisplayItem[]) => {
    if (sd.displayItems)
      for (let i = 0; i < sd.displayItems.length; i++) {
        result.push(sd.displayItems[i]);
        this.getAllDisplayItemsOfDi(sd.displayItems[i], result);
      }
  };

  getAllDisplayItemsOfDi = (di: IDisplayItem, result: IDisplayItem[]) => {
    if (di.children)
      for (let i = 0; i < di.children.length; i++) {
        result.push(di.children[i]);
        this.getAllDisplayItemsOfDi(di.children[i], result);
      }
  };

  filterDisplayItemsByValueProperty = (allDi: IDisplayItem[]): IDisplayItem[] => {
    const newDiList = [] as IDisplayItem[];
    allDi.forEach((di) => {
      const { item } = di;
      let isAdded = false;
      item.properties.forEach((element) => {
        const propertyDefinition: IPropertyDefinition | undefined = this.getPropertyDefinition(
          item,
          element,
          this.props.sd.project
        );
        if (propertyDefinition?.isValueProp && isAdded == false) {
          newDiList.push(di);
          isAdded = true;
        }
      });
    });
    return newDiList;
  };

  handleComponentSelect = (value: number) => {
    const sd = this.props.sd.getScreenDesign();
    if (value == undefined) {
      sd.onClickActionComponentId = undefined;
      this.props.sd.setScreenDesign(sd, ESdChangeReason.SetButtonOfRunOnEnter);
    } else {
      const allDi = this.getAllDi();
      sd.onClickActionComponentId = allDi[value].id.toString();
      this.props.sd.setScreenDesign(sd, ESdChangeReason.SetButtonOfRunOnEnter);
    }
  };

  getValue = () => {
    const allDi = this.getAllDi();
    let returnValue;
    allDi.map((di, index) => {
      if (di.id.toString() === this.props.sd.getScreenDesign().onClickActionComponentId) {
        returnValue = index;
      }
    });
    return returnValue;
  };

  renderComponentList = (): JSX.Element => {
    if (!this.state.isPressEnterPanelActive) {
      return <></>;
    }
    const allDi: IDisplayItem[] = this.getAllDi();
    return (
      <>
        <KuikaSmallMediumText>Select a button to run on Enter</KuikaSmallMediumText>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            allowClear
            searchable
            style={{ width: "100%" }}
            value={this.getValue()}
            data-testid="screenPressEnterSelectButtonDropdown"
            onChange={(val: any) => {
              this.handleComponentSelect(val);
            }}
            className="kuika_add_actions_inputs_size"
            options={allDi.map((di, i) => {
              return { label: di.friendlyName, value: i };
            })}
          />
        </div>
      </>
    );
  };

  render() {
    return (
      <>
        <KuikaCollapsePanel
          destroyInactivePanel
          activeKey={this.state.isPressEnterPanelActive ? "press-enter-panel" : ""}
          onChange={() => {
            this.setState({
              isPressEnterPanelActive: !this.state.isPressEnterPanelActive
            });
          }}
          iconPlacement="right"
          className="kuika-right-panel__collapse kuika-right-panel__collapse-lighter kuika-right-panel__collapse--boxed kuika-right-panel__collapse--press-enter"
          expandIcon={this.renderExpandIcon}
          items={[
            {
              key: "press-enter-panel",
              label: (
                <>
                  <PropertyRowIcon kind="pressEnter" />
                  <KuikaTextMedium data-onboarding-id="press-enter-panel-header-title">Enter Key</KuikaTextMedium>
                </>
              ),
              children: (
                <div className="kuika-right-panel__collapse-body">
                  <div className="kuika-right-panel__col kuika_property-grid-column kuika_select_property">
                    {this.renderComponentList()}
                  </div>
                </div>
              )
            }
          ]}
        />
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.SetButtonOfRunOnEnter ||
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi
    ) {
      return true;
    }
    return false;
  };
}

const pressEnterPanel = withSD<IPressEnterPanelProps>(PressEnterPanel);
export { pressEnterPanel as PressEnterPanel };
